export const SHOW_IMPORTANT_DATES = 'important_dates';
export const SHOW_ACTIVE_STAGES = 'playbook_range';
export const SHOW_STEP_DEADLINES = 'playbook_step';

export const DEFAULT_SHOW_VALUES = Object.freeze([
  SHOW_IMPORTANT_DATES,
  SHOW_ACTIVE_STAGES,
  SHOW_STEP_DEADLINES,
]);

export const SHOW_FILTER_OPTIONS = Object.freeze([
  { value: SHOW_IMPORTANT_DATES, label: 'Important dates' },
  { value: SHOW_ACTIVE_STAGES, label: 'Active stages' },
  { value: SHOW_STEP_DEADLINES, label: 'Step deadlines' },
]);

export function getCalendarSourceVisibility(showValues = DEFAULT_SHOW_VALUES) {
  const selected = new Set(showValues);
  const showImportantDates = selected.has(SHOW_IMPORTANT_DATES);
  const showActiveStages = selected.has(SHOW_ACTIVE_STAGES);
  const showStepDeadlines = selected.has(SHOW_STEP_DEADLINES);
  return {
    showImportantDates,
    showActiveStages,
    showStepDeadlines,
    needStages: showActiveStages || showStepDeadlines,
  };
}

export function restoreShowDefaultsAfterReset(values, resetRequested = false) {
  return resetRequested
    ? { ...values, show: [...DEFAULT_SHOW_VALUES] }
    : values;
}

export function isCalendarFilterResetTarget(target) {
  const button = target?.closest?.('button');
  if (!button || button.textContent?.trim() !== 'Reset all') return false;
  return Boolean(button.closest?.('aside[aria-label="Filters"]'));
}

export function createLatestRequestGuard() {
  let latestRequestId = 0;
  return {
    start() {
      latestRequestId += 1;
      return latestRequestId;
    },
    isCurrent(requestId) {
      return requestId === latestRequestId;
    },
  };
}

export function buildLegendMetrics({
  showValues,
  dateTypes,
  selectedDateTypes = [],
  stageMeta,
}) {
  const visibility = getCalendarSourceVisibility(showValues);
  const metrics = [];

  if (visibility.showImportantDates) {
    const selected = new Set(selectedDateTypes);
    dateTypes
      .filter((dateType) => selected.size === 0 || selected.has(dateType.value))
      .forEach((dateType) => metrics.push({ label: dateType.label, color: dateType.color }));
  }
  if (visibility.showActiveStages) {
    metrics.push({
      label: stageMeta[SHOW_ACTIVE_STAGES].label,
      color: stageMeta[SHOW_ACTIVE_STAGES].color,
    });
  }
  if (visibility.showStepDeadlines) {
    metrics.push({
      label: stageMeta[SHOW_STEP_DEADLINES].label,
      color: stageMeta[SHOW_STEP_DEADLINES].color,
    });
  }

  return metrics;
}

export function formatImportantDateTitle(item) {
  const name = item.talent?.full_name || 'Talent';
  if (item.date_type === 'birthday' && Number.isInteger(item.age)) {
    return `${name} — ${item.age} years old`;
  }
  if (item.date_type === 'start_work' && Number.isInteger(item.years_of_service)) {
    const unit = item.years_of_service === 1 ? 'year' : 'years';
    return `${name} — ${item.years_of_service} ${unit}`;
  }
  return name;
}

export function buildImportantDateEvent(item, {
  formatDate = (value) => value,
  formatTime = () => undefined,
  timeDateTypes = new Set(),
} = {}) {
  const occurrenceDate = item.occurrence_date || formatDate(item.date_value);
  return {
    id: `id-${item.talent.id}-${item.important_date_id}-${occurrenceDate}`,
    category: item.date_type,
    title: formatImportantDateTitle(item),
    date: occurrenceDate,
    ...(timeDateTypes.has(item.date_type) && { time: formatTime(item.date_value) }),
    data: {
      kind: 'important_date',
      talentId: item.talent.id,
      dateTypeLabel: item.date_type_label,
      isUpcoming: item.is_upcoming,
      description: item.description || null,
      talentType: item.talent.talent_type,
      status: item.talent.current_status || null,
      managerId: item.talent.manager_id || null,
      manager: item.talent.manager || null,
      profession: item.talent.profession || null,
      dateValue: item.date_value,
      occurrenceDate,
      isRecurringOccurrence: Boolean(item.is_recurring_occurrence),
      age: item.age ?? null,
      yearsOfService: item.years_of_service ?? null,
      hasTime: timeDateTypes.has(item.date_type),
    },
  };
}

export function toInclusiveNavigationRange(currentValues, { start, end }) {
  return {
    ...currentValues,
    importantDatesRange: { start, end },
  };
}
